import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box, Typography, Button, Chip, CircularProgress, LinearProgress, Alert, Avatar, Switch, TextField, MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TenantAdminActions from "./components/TenantAdminActions";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { getTenantById, getUsersByTenant, updateTenant, getAnalyticsTenantFeatures, getTenantUsageHealth, getCarrierOnboardingDirectory } from "./api/tenantApi";
import { isPlatformAdmin } from "./utils/isPlatformAdmin";
import { buildCarrierInsights } from "./utils/carrierInsights";
import { getReportingPortfolio } from "./api/underwritingApi";
import TenantUsersPanel from "./TenantUsersPanel";
import { deriveTenantUsage } from "./utils/tenantUsage";
import CarrierOnboardingFileDialog from "./components/CarrierOnboardingFileDialog";
import PaymentPlatformDataCenterWorkspace from "./components/PaymentPlatformDataCenterWorkspace";
import AdminMcaModelChat from "./components/AdminMcaModelChat";
import AdminMcaModelPipeline from "./components/AdminMcaModelPipeline";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3", AMBER = "#E7A13C", RED = "#E24B4A";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
// Robustly turn any timestamp shape into a Date — ISO strings, epoch numbers, JS
// Dates, and serialized Firestore Timestamps ({_seconds}/{seconds}). The backend
// now sends ISO, but this is a safety net for any raw/cached Timestamp objects.
const parseTs = (v) => {
  if (!v) return null;
  if (typeof v === "object") {
    const secs = v._seconds ?? v.seconds;
    if (typeof secs === "number") return new Date(secs * 1000);
  }
  const d = new Date(v);
  return isNaN(d) ? null : d;
};
const fmtDate = (v) => { const d = parseTs(v); return d ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"; };
const fmtRelative = (v) => {
  if (!v) return "Never";
  const d = new Date(v); if (isNaN(d)) return "Never";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const lastLoginOf = (users) => {
  const times = (users || []).map((u) => u.last_login).filter(Boolean).map((t) => new Date(t).getTime()).filter((n) => !isNaN(n));
  return times.length ? new Date(Math.max(...times)).toISOString() : null;
};
const bandColor = (label) => (/high/i.test(label) ? RED : /mod/i.test(label) ? AMBER : /low/i.test(label) ? GREEN : MUTED);
const initials = (s) => (s || "?").trim().slice(0, 2).toUpperCase();

const Card = ({ title, description, action, children, sx, trackArea }) => (
  <Box component="section" data-track-area={trackArea || title} sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", background: "#fff", p: { xs: 2, md: 2.5 }, boxShadow: "0 1px 3px rgba(11,42,74,.035)", ...sx }}>
    {title && (
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: description ? 2 : 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 800, color: INK }}>{title}</Typography>
          {description && <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.45, mt: .35 }}>{description}</Typography>}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const Kpi = ({ label, value, sub, icon, tone = BLUE, progress }) => (
  <Box data-track-area={`${label} tenant summary`} sx={{ border: `1px solid ${LINE}`, borderRadius: "15px", p: 2, background: "#fff", boxShadow: "0 1px 3px rgba(11,42,74,.03)" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      {icon && <Box sx={{ width: 32, height: 32, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: tone, bgcolor: `${tone}12` }}>{icon}</Box>}
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: MUTED }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, lineHeight: 1.1, letterSpacing: "-.02em" }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12, color: MUTED, mt: .4 }}>{sub}</Typography>}
    {progress != null && <LinearProgress variant="determinate" value={progress} aria-label={`${label}: ${progress}%`} sx={{ mt: 1.25, height: 5, borderRadius: 3, bgcolor: "#e8eef5", "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: progress > 85 ? AMBER : tone } }} />}
  </Box>
);

const Row = ({ k, v }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.85, borderBottom: `1px solid #f4f7fb` }}>
    <Typography sx={{ fontSize: 13, color: MUTED }}>{k}</Typography>
    <Typography sx={{ fontSize: 13, color: INK, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{v}</Typography>
  </Box>
);

const healthColor = (state) => state === "Healthy" ? GREEN
  : state === "Softening" ? AMBER
    : state === "At-Risk" || state === "Dormant" ? RED : MUTED;

const metricLabel = (metric) => ({
  reportsPulled: "Reports pulled", apiCalls: "API calls", activeUsers: "Active users",
  daysSinceLastActivity: "Days since activity",
}[metric] || metric || "—");

const Sparkline = ({ values = [], color = BLUE }) => {
  const max = Math.max(1, ...values.map((value) => Number(value) || 0));
  return (
    <Box sx={{ height: 42, display: "flex", alignItems: "flex-end", gap: "2px" }} aria-label={`Sparkline: ${values.join(", ")}`}>
      {values.map((value, index) => <Box key={index} sx={{ flex: 1, minWidth: 2, height: `${Math.max(3, Math.round(((Number(value) || 0) / max) * 100))}%`, bgcolor: color, opacity: 0.8, borderRadius: "2px 2px 0 0" }} />)}
    </Box>
  );
};

// Admin-editable tenant boolean flag (e.g. Paid account). Saves on toggle.
function PlanFlag({ tenantId, label, field, on, onChange }) {
  const [saving, setSaving] = useState(false);
  const toggle = async (e) => {
    const val = e.target.checked;
    setSaving(true);
    try {
      await updateTenant(tenantId, { [field]: val });
      onChange(field, val);
      toast.success(`${label} ${val ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.35 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {on ? <CheckCircleIcon sx={{ fontSize: 18, color: GREEN }} /> : <CancelIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />}
        <Typography sx={{ fontSize: 13.5, color: on ? INK : MUTED }}>{label}</Typography>
      </Box>
      {saving ? <CircularProgress size={16} /> : <Switch size="small" checked={!!on} onChange={toggle} />}
    </Box>
  );
}

function TenantTypeEditor({ tenant, onChanged }) {
  const [saving, setSaving] = useState(false);
  const saveType = async (tenantType) => {
    setSaving(true);
    const patch = tenantType === "payment_platform"
      ? { tenantType, assessmentCategory: "mca_loan", isDNBSearchEnabled: false, isDNBFinancialDataEnabled: false }
      : { tenantType, assessmentCategory: "trade_credit" };
    try {
      const response = await updateTenant(tenant.id, patch);
      onChanged(response?.data || response || patch);
      toast.success("Tenant workflow updated");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update tenant type");
    } finally { setSaving(false); }
  };
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mb: 1.5 }}>
      <TextField select size="small" label="Tenant type" value={tenant.tenantType || "regular"} disabled={saving} onChange={(event) => saveType(event.target.value)}>
        <MenuItem value="regular">Regular credit tenant</MenuItem>
        <MenuItem value="payment_platform">Payment platform</MenuItem>
      </TextField>
      <TextField select size="small" label="Assessment category" value={tenant.assessmentCategory || (tenant.tenantType === "payment_platform" ? "mca_loan" : "trade_credit")} disabled>
        <MenuItem value="trade_credit">Trade credit</MenuItem>
        <MenuItem value="mca_loan">MCA / loans</MenuItem>
      </TextField>
    </Box>
  );
}

export default function TenantDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const email = useSelector((s) => s.userInfo.email);
  const platformAdmin = isPlatformAdmin(email, isAdmin);

  const [tenant, setTenant] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [usageHealth, setUsageHealth] = useState(null);
  const [carrierInsights, setCarrierInsights] = useState(null);
  const [carrierError, setCarrierError] = useState("");
  const [onboardingFileOpen, setOnboardingFileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [tRes, pRes, uRes, fRes, hRes, cRes] = await Promise.all([
        getTenantById(tid),
        getReportingPortfolio(tid).catch(() => null),
        getUsersByTenant(tid).catch(() => ({ data: [] })),
        getAnalyticsTenantFeatures(tid, { days: 90 }).catch(() => []),
        getTenantUsageHealth(tid).catch(() => null),
        getCarrierOnboardingDirectory()
          .then((data) => ({ data, error: "" }))
          .catch((requestError) => ({ data: null, error: requestError?.response?.data?.error || requestError?.message || "Carrier data is temporarily unavailable" })),
      ]);
      const nextTenant = tRes?.data || tRes || null;
      setTenant(nextTenant);
      setPortfolio(pRes?.data || pRes || null);
      setUsers(uRes?.data || []);
      setFeatures(Array.isArray(fRes) ? fRes : []);
      setUsageHealth(hRes || null);
      setCarrierInsights(cRes.data ? buildCarrierInsights(nextTenant, cRes.data) : null);
      setCarrierError(cRes.error);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load tenant");
    } finally { setLoading(false); }
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  const agg = portfolio?.aggregates;
  // "Recent activity" = most recently assessed. Sort by last assessment (updatedAt),
  // falling back to when the counterparty was added. parseTs handles any timestamp shape.
  const activityTs = (r) => { const d = parseTs(r.updatedAt || r.createdAt); return d ? d.getTime() : 0; };
  const recentRows = useMemo(() => {
    const rows = portfolio?.rows || [];
    return [...rows].sort((a, b) => activityTs(b) - activityTs(a)).slice(0, 8);
  }, [portfolio]);

  if (!platformAdmin) return <Box sx={{ p: 6, textAlign: "center", color: MUTED }}>CrediArc platform administrators only.</Box>;

  if (loading) return <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 10, gap: 1.5 }}><CircularProgress size={24} /><Typography sx={{ color: MUTED }}>Loading tenant…</Typography></Box>;

  if (error || !tenant) return (
    <Box sx={{ maxWidth: 700, mx: "auto", p: 4 }}>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />} onClick={() => navigate("/platform")} sx={{ textTransform: "none", mb: 2 }}>Back to Platform Console</Button>
      <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error || "Tenant not found"}</Alert>
    </Box>
  );

  const { used, limit: cap, pct } = deriveTenantUsage(tenant.counter, tenant.maxCounterLimit);

  return (
    <Box data-track-area="Tenant administration page" sx={{ bgcolor: "#f5f7fa", minHeight: "100%", py: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3.5 } }}>
        <Button startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />} onClick={() => navigate("/platform")}
          aria-label="Back to tenant directory" sx={{ textTransform: "none", color: MUTED, fontWeight: 650, mb: 1.5 }}>
          Tenant directory
        </Button>

        {/* Account identity and primary destinations */}
        <Box component="header" data-track-area="Tenant identity header" sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2.5, flexWrap: "wrap",
          border: `1px solid ${LINE}`, borderRadius: "18px", bgcolor: "#fff", px: { xs: 2, md: 2.75 }, py: { xs: 2, md: 2.5 }, mb: 2,
          boxShadow: "0 2px 7px rgba(11,42,74,.04)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 }, minWidth: 0 }}>
            <Avatar sx={{ width: { xs: 50, md: 60 }, height: { xs: 50, md: 60 }, bgcolor: BLUE, fontWeight: 800, fontSize: 20 }}>{initials(tenant.name)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".09em", color: BLUE, mb: .4 }}>Tenant account</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: .8, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ fontSize: { xs: 25, md: 30 }, fontWeight: 800, color: INK, letterSpacing: "-.025em", lineHeight: 1.15 }}>{tenant.name || "Tenant"}</Typography>
                <Chip size="small" label={tenant.isPaid ? "Paid" : "Trial"} sx={{ height: 22, fontWeight: 750, color: tenant.isPaid ? "#15803d" : "#92610a", background: tenant.isPaid ? "#dcfce7" : "#fef3c7" }} />
                {tenant.isAREnabled && <Chip size="small" label="AR enabled" sx={{ height: 22, background: "#eef2ff", color: "#3b3f8f", fontWeight: 700 }} />}
                {tenant.tenantType === "payment_platform" && <Chip size="small" label="Payment platform · MCA / loans" sx={{ height: 22, background: "#e0f2fe", color: "#075985", fontWeight: 700 }} />}
              </Box>
              <Typography title={tenant.id} sx={{ fontSize: 12, color: MUTED, fontFamily: "monospace", mt: .45, overflow: "hidden", textOverflow: "ellipsis" }}>{tenant.id}</Typography>
            </Box>
          </Box>
          <Box data-track-area="Tenant primary actions" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button onClick={() => navigate(`/tenant-dashboard/${tenant.id}/reporting`)} startIcon={<InsightsIcon />} variant="outlined"
              sx={{ textTransform: "none", borderColor: "#cbd7e5", borderRadius: "10px", color: INK, fontWeight: 700 }}>BI &amp; reporting</Button>
            {tenant.tenantType === "payment_platform" && (
              <Button
                onClick={() => document.getElementById("tenant-document-center")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                startIcon={<FolderOpenOutlinedIcon />}
                variant="outlined"
                sx={{ textTransform: "none", borderColor: "#cbd7e5", borderRadius: "10px", color: INK, fontWeight: 700 }}
              >
                Document center
              </Button>
            )}
            <Button onClick={() => window.open(`/tenant-dashboard/${tenant.id}`, "_blank", "noopener,noreferrer")} startIcon={<OpenInNewIcon />} variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", bgcolor: BLUE, fontWeight: 750, boxShadow: "0 4px 10px rgba(30,111,232,.18)" }}>Open dashboard</Button>
          </Box>
        </Box>

        {/* The four operational signals admins need before taking action. */}
        <Box component="section" aria-label="Tenant overview" data-track-area="Tenant overview" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, minmax(0,1fr))" }, gap: 1.25, mb: 2 }}>
          <Kpi label="Team members" value={users.length || tenant.emailList?.length || 0} sub="People with account access" tone="#7557D9" icon={<GroupOutlinedIcon sx={{ fontSize: 18 }} />} />
          <Kpi label="Counterparties" value={fmt(agg?.counterparties ?? used)} sub="Assessed in the risk platform" tone={GREEN} icon={<BusinessOutlinedIcon sx={{ fontSize: 18 }} />} />
          <Kpi label="Account usage" value={`${fmt(used)} / ${cap ? fmt(cap) : "∞"}`} sub={cap ? `${pct}% of configured capacity` : "Unlimited capacity"} progress={cap ? pct : null} icon={<DataUsageOutlinedIcon sx={{ fontSize: 18 }} />} />
          <Kpi label="Last entrance" value={fmtRelative(lastLoginOf(users))} sub="Most recent user sign-in" tone="#C77B13" icon={<LoginOutlinedIcon sx={{ fontSize: 18 }} />} />
        </Box>

      {/* Admin actions — the full per-tenant capability set (shared with the console table menu) */}
      <Card title="Administration tools" description="Choose a task by purpose. Account access and user actions live in Team members." trackArea="Tenant administration tools card" sx={{ mb: 2 }}
        action={<SettingsOutlinedIcon sx={{ color: "#8a9aab", fontSize: 22 }} />}>
        <TenantAdminActions
          variant="buttons"
          tenant={tenant}
          onChanged={(updated) => setTenant((t) => ({ ...t, ...updated }))}
        />
      </Card>

      {tenant.tenantType === "payment_platform" && (
        <>
          <Card
            title="MCA model builder"
            description="Use ArcAI or build an executable cleaning, time-series, and post-rule pipeline with measured portfolio results."
            trackArea="Admin MCA model chatbot"
            sx={{ mb: 2 }}
          >
            <AdminMcaModelChat tenant={tenant} />
            <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 2.5, pt: 2.5 }}>
              <AdminMcaModelPipeline tenantId={tenant.id} />
            </Box>
          </Card>
          <Box
            component="section"
            id="tenant-document-center"
            data-track-area="Tenant document center"
            sx={{ mb: 2, scrollMarginTop: 24 }}
          >
            <PaymentPlatformDataCenterWorkspace
              tenantId={tenant.id}
              title="Document center"
              description="Manage the tenant's MCA and loan source files, labels, downloads, and model inputs from one place."
              uploadLabel="Upload documents"
              uploadDialogTitle="Upload tenant documents"
              uploadDialogDescription="Label each file as input, metadata, or output so the MCA model can use it correctly."
              source="platform_admin_upload"
            />
          </Box>
        </>
      )}

      <Card
        title="Usage health"
        description="Adoption signals, activity trend, and the metric currently influencing account health."
        action={usageHealth && <Chip size="small" label={usageHealth.state} sx={{ height: 23, fontWeight: 800, color: healthColor(usageHealth.state), bgcolor: `${healthColor(usageHealth.state)}18` }} />}
        sx={{ mb: 2 }}
      >
        {!usageHealth ? (
          <Typography sx={{ fontSize: 13, color: MUTED }}>Health has not been computed yet. The nightly monitor will populate this tenant.</Typography>
        ) : (
          <>
            {usageHealth.onboardingUnscored && <Alert severity="info" sx={{ mb: 1.5 }}>Onboarding, unscored — {usageHealth.historyDays} of {usageHealth.config?.minimumHistoryDays || 60} required history days.</Alert>}
            {usageHealth.seasonalityExempt && <Alert severity="warning" sx={{ mb: 1.5 }}>Seasonality exempt — state is computed, but transition alerts are suppressed.</Alert>}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
              {[
                ["Reports · 7d", usageHealth.windows?.["7d"]?.reportsPulled],
                ["API calls · 7d", usageHealth.windows?.["7d"]?.apiCalls],
                ["Active users · 7d", usageHealth.windows?.["7d"]?.activeUsers],
                ["Last activity", usageHealth.daysSinceLastActivity == null ? "Never" : `${usageHealth.daysSinceLastActivity}d ago`],
              ].map(([label, value]) => <Box key={label}><Typography sx={{ fontSize: 11, color: MUTED, textTransform: "uppercase", fontWeight: 700 }}>{label}</Typography><Typography sx={{ fontSize: 21, color: INK, fontWeight: 800 }}>{value ?? 0}</Typography></Box>)}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${LINE}` }}>
              <Box>
                <Row k="Metric that moved" v={metricLabel(usageHealth.movedMetric)} />
                <Row k="90d weekly median" v={usageHealth.movedMetric && usageHealth.movedMetric !== "daysSinceLastActivity" ? fmt(usageHealth.baseline?.[usageHealth.movedMetric]) : "—"} />
                <Row k="Computed" v={fmtRelative(usageHealth.computedAt)} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textTransform: "uppercase", fontWeight: 700, mb: 0.5 }}>28-day activity</Typography>
                <Sparkline values={(usageHealth.sparkline || []).map((row) => (row.reportsPulled || 0) + (row.apiCalls || 0) + (row.activeUsers || 0))} color={healthColor(usageHealth.state)} />
              </Box>
            </Box>
          </>
        )}
      </Card>

      {/* Keep account access and user management next to the tenant's usage
          status so admins can move directly from health review to support. */}
      <Box sx={{ mb: 2 }}>
        <TenantUsersPanel tenant={tenant} carrierInsights={carrierInsights} onChanged={load} />
      </Box>

      <Card
        title="Carrier & Surety"
        description="Linked carrier workspace, onboarding progress, access, and recent journey activity."
        action={carrierInsights ? (
          <Chip size="small" icon={<ShieldOutlinedIcon />} label={carrierInsights.status} sx={{ height: 25, fontWeight: 800, color: carrierInsights.status === "Active" ? GREEN : AMBER, bgcolor: carrierInsights.status === "Active" ? "#dcfce7" : "#fef3c7" }} />
        ) : null}
        sx={{ mb: 2 }}
      >
        {carrierError ? (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            Risk activity is available, but carrier/surety activity could not be loaded: {carrierError}
          </Alert>
        ) : !carrierInsights ? (
          <Alert severity="info">
            No carrier/surety workspace is linked to this tenant yet. Use <strong>Surety onboarding</strong> above to create or connect one.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Box>
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: 17 }}>{carrierInsights.company.name}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>{carrierInsights.company.company_type || "Carrier & Surety Client"}{carrierInsights.company.industry ? ` · ${carrierInsights.company.industry}` : ""}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button onClick={() => setOnboardingFileOpen(true)} startIcon={<DescriptionOutlinedIcon />} variant="outlined" size="small" sx={{ textTransform: "none", borderColor: LINE, color: INK }}>
                  View onboarding file
                </Button>
                <Button href="https://management.crediarc.net/" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} variant="outlined" size="small" sx={{ textTransform: "none", borderColor: LINE, color: INK }}>
                  Open carrier platform
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 2 }}>
              <Kpi label="Carrier plan" value={carrierInsights.plan.replace(" Carrier & Surety", "")} sub="Carrier & Surety" />
              <Kpi label="Journey stage" value={carrierInsights.status} sub={carrierInsights.onboardingStatus} />
              <Kpi label="Onboarding" value={`${carrierInsights.completionPercent}%`} sub={`Section ${carrierInsights.onboarding?.current_section ?? 0} of 34`} />
              <Kpi label="Carrier users" value={carrierInsights.users.length} sub={`${carrierInsights.activeUsers30d} active · 30d`} />
              <Kpi label="Carrier logins · 30d" value={fmt(carrierInsights.logins30d)} sub={carrierInsights.lastActivity ? `Last ${fmtRelative(carrierInsights.lastActivity)}` : "No activity yet"} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Surety onboarding progress</Typography>
                <Typography sx={{ color: INK, fontSize: 12.5, fontWeight: 700 }}>{carrierInsights.completionPercent}% · {carrierInsights.onboardingStatus}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={carrierInsights.completionPercent} sx={{ height: 7, borderRadius: 4, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: carrierInsights.completionPercent === 100 ? GREEN : BLUE } }} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.8fr 1.7fr" }, gap: 2, pt: 2, borderTop: `1px solid ${LINE}` }}>
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textTransform: "uppercase", fontWeight: 700, mb: 0.75 }}>Carrier users & roles</Typography>
                {carrierInsights.users.length === 0 ? <Typography sx={{ fontSize: 13, color: MUTED }}>No carrier users yet.</Typography> : carrierInsights.users.map((user) => (
                  <Box key={user.user_id} sx={{ py: 0.8, borderBottom: "1px solid #f4f7fb" }}>
                    <Typography sx={{ fontSize: 13, color: INK, fontWeight: 700 }}>{user.full_name || user.email}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: MUTED }}>{user.email}</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                      {(user.roles || []).map((role) => <Chip key={role} size="small" label={role.replaceAll("_", " ")} sx={{ height: 19, fontSize: 10.5, textTransform: "capitalize" }} />)}
                      <Chip size="small" label={(user.account_stage || "unknown").replaceAll("_", " ")} sx={{ height: 19, fontSize: 10.5, textTransform: "capitalize", color: user.account_stage === "active" ? GREEN : MUTED }} />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textTransform: "uppercase", fontWeight: 700, mb: 0.75 }}>Carrier & surety journey</Typography>
                {carrierInsights.journey.length === 0 ? <Typography sx={{ fontSize: 13, color: MUTED }}>No carrier journey events yet.</Typography> : carrierInsights.journey.slice(0, 12).map((event, index) => (
                  <Box key={`${event.kind}-${event.timestamp}-${index}`} sx={{ display: "grid", gridTemplateColumns: "10px minmax(0, 1fr) auto", gap: 1, alignItems: "start", py: 0.75, borderBottom: "1px solid #f4f7fb" }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: event.kind === "login" ? BLUE : event.kind === "activation" ? GREEN : AMBER, mt: 0.6 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, color: INK, fontWeight: 700 }}>{event.label}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{event.detail}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>{fmtRelative(event.timestamp)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Card>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" }, gap: 2, alignItems: "start" }}>
        {/* left column */}
        <Box sx={{ display: "grid", gap: 2 }}>
          {/* usage & portfolio */}
          <Card title="Portfolio overview" description="Capacity, exposure, decisions, and current risk distribution.">
            {cap ? (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>Counterparties this period</Typography>
                  <Typography sx={{ fontSize: 13, color: INK, fontWeight: 700 }}>{fmt(used)} / {fmt(cap)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 4, backgroundColor: "#eef2f7", mb: 2, "& .MuiLinearProgress-bar": { backgroundColor: pct > 85 ? AMBER : BLUE } }} />
              </>
            ) : null}
            {agg ? (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
                  <Box><Typography sx={{ fontSize: 12, color: MUTED }}>Average ARC</Typography><Typography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>{agg.avgScore != null ? Number(agg.avgScore).toFixed(1) : "—"}</Typography></Box>
                  <Box><Typography sx={{ fontSize: 12, color: MUTED }}>Recommended</Typography><Typography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>{fmtUSD(agg.totalRecommendedExposure)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: 12, color: MUTED }}>Approved</Typography><Typography sx={{ fontSize: 16, fontWeight: 700, color: GREEN }}>{fmtUSD(agg.totalApprovedExposure)}</Typography></Box>
                  <Box><Typography sx={{ fontSize: 12, color: MUTED }}>Pending / approved</Typography><Typography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>{fmt(agg.pending)} / {fmt(agg.approved)}</Typography></Box>
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: MUTED, mb: 1 }}>Risk distribution</Typography>
                {(agg.byBand || []).map((b) => {
                  const share = agg.counterparties ? Math.round((b.count / agg.counterparties) * 100) : 0;
                  return (
                    <Box key={b.name} sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                        <Typography sx={{ fontSize: 12.5, color: INK }}>{b.name}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmt(b.count)} · {share}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={share} sx={{ height: 6, borderRadius: 3, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: bandColor(b.name) } }} />
                    </Box>
                  );
                })}
              </>
            ) : <Typography sx={{ fontSize: 13, color: MUTED }}>Portfolio analytics unavailable for this tenant.</Typography>}
          </Card>

          {/* recent activity */}
          <Card title="Risk platform activity" description="The most recently assessed counterparties." action={<Typography sx={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>Latest first</Typography>}>
            {recentRows.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>No recent activity.</Typography>
            ) : recentRows.map((r, i) => (
              <Box key={i} sx={{ display: "grid", gridTemplateColumns: "2fr 0.6fr 1fr 1fr", gap: 1, alignItems: "center", py: 0.9, borderTop: i ? `1px solid #f4f7fb` : "none" }}>
                <Typography sx={{ fontSize: 13, color: INK, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</Typography>
                <Chip size="small" label={r.score != null ? r.score.toFixed(1) : "—"} sx={{ height: 19, fontSize: 11, fontWeight: 700, background: "#f1f5f9", color: INK, justifySelf: "start" }} />
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmtUSD(Math.round(r.recLimit || 0))}</Typography>
                <Typography sx={{ fontSize: 12, color: MUTED, textAlign: "right" }}>{fmtDate(r.updatedAt || r.createdAt)}</Typography>
              </Box>
            ))}
          </Card>
        </Box>

        {/* right column */}
        <Box sx={{ display: "grid", gap: 2 }}>
          <Card title="Plan & features" description="Workflow, entitlements, scoring engine, and account limits." action={<Typography sx={{ fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>Changes save immediately</Typography>}>
            <TenantTypeEditor tenant={tenant} onChanged={(updated) => setTenant((current) => ({ ...current, ...updated }))} />
            {(() => {
              const upd = (f, v) => {
                setTenant((t) => ({ ...t, [f]: v }));
                if (f === "seasonality_exempt") setUsageHealth((h) => h ? ({ ...h, seasonalityExempt: v }) : h);
              };
              return (
                <>
                  <PlanFlag tenantId={tenant.id} label="Paid account" field="isPaid" on={tenant.isPaid} onChange={upd} />
                  <PlanFlag tenantId={tenant.id} label="Accounts Receivable" field="isAREnabled" on={tenant.isAREnabled} onChange={upd} />
                  {tenant.tenantType !== "payment_platform" && <PlanFlag tenantId={tenant.id} label="D&B company search" field="isDNBSearchEnabled" on={tenant.isDNBSearchEnabled} onChange={upd} />}
                  {tenant.tenantType !== "payment_platform" && <PlanFlag tenantId={tenant.id} label="D&B financial data" field="isDNBFinancialDataEnabled" on={tenant.isDNBFinancialDataEnabled} onChange={upd} />}
                  <PlanFlag tenantId={tenant.id} label="Custom prompts" field="isCustomPromptsEnabled" on={tenant.isCustomPromptsEnabled} onChange={upd} />
                  <PlanFlag tenantId={tenant.id} label="Quick Tasks table" field="isQuickTasksEnabled" on={tenant.isQuickTasksEnabled} onChange={upd} />
                  <PlanFlag tenantId={tenant.id} label="Insurance" field="isInsuranceEnabled" on={tenant.isInsuranceEnabled} onChange={upd} />
                  <PlanFlag tenantId={tenant.id} label="AR+ (risk & collections)" field="isArPlusEnabled" on={tenant.isArPlusEnabled} onChange={upd} />
                  <PlanFlag tenantId={tenant.id} label="Seasonality exempt" field="seasonality_exempt" on={tenant.seasonality_exempt} onChange={upd} />
                </>
              );
            })()}
            <Box sx={{ mt: 1.5 }}>
              <Row k="AI engine" v={tenant.aiEngine || "default"} />
              <Row k="Max counterparties" v={cap ? fmt(cap) : "Unlimited"} />
              <Row k="Quick Tasks size" v={`${tenant.quickTasksConfig?.maxRows || 5} rows`} />
              {tenant.arThreshold != null && <Row k="AR threshold" v={tenant.arThreshold} />}
            </Box>
          </Card>

          {/* Feature usage — which product areas this tenant has actually used (last 90 days) */}
          <Card title="Feature adoption" description="Which product areas this account has actually used." action={<Typography sx={{ fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>Last 90 days</Typography>}>
            {features.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>No usage captured yet.</Typography>
            ) : (
              [...features].sort((a, b) => (b.used - a.used) || ((b.lastSeen || 0) - (a.lastSeen || 0))).map((f) => (
                <Box key={f.key} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.55 }}>
                  {f.used
                    ? <CheckCircleIcon sx={{ fontSize: 18, color: GREEN }} />
                    : <CancelIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />}
                  <Typography sx={{ fontSize: 13.5, color: f.used ? INK : MUTED }}>{f.label}</Typography>
                  {f.used && (
                    <Typography sx={{ fontSize: 12, color: MUTED, ml: "auto" }}>
                      {fmtRelative(f.lastSeen)} · {fmt(f.count)}×
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Card>

          <Card title="Account details" description="Reference information and account record timestamps.">
            <Row k="Contact email" v={tenant.email || "—"} />
            <Row k="Tenant ID" v={<span style={{ fontFamily: "monospace", fontSize: 11.5 }}>{tenant.id}</span>} />
            <Row k="Created" v={fmtDate(tenant.metadata?.createdAt)} />
            <Row k="Updated" v={fmtDate(tenant.metadata?.updatedAt)} />
            <Row k="Status" v={tenant.metadata?.status || "active"} />
          </Card>
        </Box>
      </Box>
      <CarrierOnboardingFileDialog open={onboardingFileOpen} onClose={() => setOnboardingFileOpen(false)} carrierInsights={carrierInsights} />
      </Box>
    </Box>
  );
}
